import { useParams, useLocation, Navigate, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Tag, AlertTriangle, MoreVertical, Edit2, Trash, File, Image, Video, Play, Paperclip, ChevronLeft, ChevronRight, Heart, MessageCircle, Wand2 } from "lucide-react";
import { Link } from "react-router-dom";
import NavBar from "../components/NavBar";
import { deleteWill, getWillById } from "../services/willService";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useEffect, useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { extractRewardInfo } from "../utils/currencyUtils";
import { containsHebrew } from "../utils/textUtils";
import '../styles/media.css';
import MediaViewer from "../components/MediaViewer";
import MediaDisplay from "../components/MediaDisplay";
import DownloadableFileAttachment from '../components/DownloadableFileAttachment';
import ShareMenu from '../components/ShareMenu';
import CommentModal from '../components/CommentModal';
import OfferFormModal from '../components/OfferFormModal';
import { Avatar } from "@/components/ui/avatar";
import { getFullProfileImageUrl } from "../contexts/ProfileContext";

interface LocationState {
  will: {
    id: string;
    title: string;
    description: string;
    category: string;
    urgency: string;
    reward?: string;
    fulfillmentTime?: string;
    wanterName?: string;
    createdAt?: string;
    location?: string;
    skills?: string[];
    status?: string;
    wanterId: string;
    media?: {
      id: string;
      url: string;
      name: string;
      type: string;
      mimeType: string;
      size?: number;
    }[];
  };
}

interface WillData {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  budget?: number;
  currency?: string;
  fulfillmentTime?: string;
  wanterName?: string;
  createdAt: string;
  location?: string;
  skills?: string[];
  wanterId: string;
  urgency?: string;
  media?: {
    id: string;
    url: string;
    name: string;
    type: string;
    mimeType: string;
    size?: number;
  }[];
}

const WillView = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showMediaViewer, setShowMediaViewer] = useState(false);
  const [mediaViewerIndex, setMediaViewerIndex] = useState(0);
  const [willData, setWillData] = useState<WillData | LocationState['will'] | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState<'left' | 'right' | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [liking, setLiking] = useState(false);
  
  const baseUrl = 'http://localhost:8080/';

  const getCurrencySymbol = (currency: string | null | undefined): string => {
    if (!currency) return '₪';
    const symbolMap: { [key: string]: string } = {
      'ILS': '₪',
      'USD': '$',
      'EUR': '€',
      'GBP': '£'
    };
    return symbolMap[currency] || '₪';
  };

  const extractCurrencyCode = (reward: string | null): string => {
    if (!reward) return 'ILS';
    const symbol = reward.replace(/[0-9]/g, '').trim();
    const symbolToCode: { [key: string]: string } = {
      '₪': 'ILS',
      '$': 'USD',
      '€': 'EUR',
      '£': 'GBP'
    };
    return symbolToCode[symbol] || 'ILS';
  };
  
  const stateFromLocation = location.state as LocationState | null;
  
  // Process media URLs to ensure they're absolute
  const processMedia = (mediaItems: any[] = []) => {
    return mediaItems.map(m => ({
      ...m,
      url: m.url ? (m.url.startsWith('http') ? m.url : `${baseUrl}${m.url}`) : '',
      type: m.type || (m.mimeType?.startsWith('image/') ? 'image' : 
                      m.mimeType?.startsWith('video/') ? 'video' : 'document'),
    }));
  };

  // Add a function to get the profile picture URL
  const getProfilePictureUrl = (profilePicture?: string): string | undefined => {
    if (!profilePicture) return undefined;
    return getFullProfileImageUrl(profilePicture);
  };

  // Handle data from location state
  useEffect(() => {
    if (stateFromLocation?.will) {
      // Process the will data from state
      const processedWill = {
        ...stateFromLocation.will,
        media: processMedia(stateFromLocation.will.media)
      };
      setWillData(processedWill);
      setLoading(false);
    }
  }, [stateFromLocation]);
  
  // Only fetch from API if no data in state
  const { isLoading: queryLoading, data: will } = useQuery({
    queryKey: ["will", id],
    queryFn: () => getWillById(id!),
    enabled: !!id && !stateFromLocation?.will,
  });
  
  // Handle data from API
  useEffect(() => {
    if (will && !stateFromLocation?.will) {
      const processedWill = {
        ...will,
        media: processMedia(will.media)
      };
      setWillData(processedWill);
      setLoading(false);
    }
  }, [will]);
  
  // Compute visual media
  const visualMedia = useMemo(() => {
    if (!willData?.media) return [];
    
    return willData.media.filter(m => {
      const mimeType = m.mimeType || '';
      return mimeType.startsWith('image/') || mimeType.startsWith('video/');
    });
  }, [willData]);
  
  // Compute document media
  const documentMedia = useMemo(() => {
    if (!willData?.media) return [];
    
    return willData.media.filter(m => {
      const mimeType = m.mimeType || '';
      return !mimeType.startsWith('image/') && !mimeType.startsWith('video/');
    });
  }, [willData]);
  
  // Get current media
  const currentMedia = visualMedia[currentImageIndex] || null;
  
  if (isLoading || queryLoading || !willData) {
    return (
      <div className="min-h-screen bg-white">
        <NavBar />
        <main className="container mx-auto px-4 pt-16 pb-8">
          <div className="max-w-2xl mx-auto text-center">
            <p>Loading want details...</p>
          </div>
        </main>
      </div>
    );
  }

  const urgencyColors = {
    low: "bg-green-100 text-green-800",
    medium: "bg-yellow-100 text-yellow-800",
    high: "bg-red-100 text-red-800"
  };

  const displayData = {
    id: willData.id,
    title: willData.title,
    description: willData.description,
    category: willData.category,
    createdAt: willData.createdAt || new Date().toISOString(),
    wanterName: willData.wanterName,
    location: willData.location,
    status: 'status' in willData ? willData.status : 'PENDING',
    skills: willData.skills || [],
    wanterId: willData.wanterId,
    fulfillmentTime: willData.fulfillmentTime,
    urgency: 'urgency' in willData ? willData.urgency : 'medium',
    media: willData.media,
    reward: (() => {
      if ('budget' in willData && 'currency' in willData && willData.budget && willData.currency) {
        return `${getCurrencySymbol(willData.currency)}${willData.budget}`;
      }
      if ('reward' in willData && willData.reward) {
        return willData.reward;
      }
      return null;
    })(),
  };

  const isOwner = () => {
    const userId = localStorage.getItem('userId');
    return userId === displayData.wanterId;
  };

  const handleDelete = async () => {
    try {
      await deleteWill(id!);
      toast.success("Want deleted successfully");
      navigate('/');
    } catch (error) {
      console.error('Failed to delete want:', error);
      toast.error("Failed to delete want");
    }
  };

  const isLoggedIn = () => {
    return !!localStorage.getItem('accessToken');
  };

  const getBudgetAndCurrency = () => {
    if ('budget' in willData && 'currency' in willData) {
      return {
        budget: willData.budget || 0,
        currency: willData.currency || 'ILS'
      };
    } else if (displayData.reward) {
      const { price, currency } = extractRewardInfo(displayData.reward);
      return {
        budget: Number(price) || 0,
        currency
      };
    }
    return { budget: 0, currency: 'ILS' };
  };

  const { budget, currency } = getBudgetAndCurrency();

  const getTextDirection = (text: string | undefined): "rtl" | "ltr" => {
    if (!text) return "ltr";
    return containsHebrew(text) ? "rtl" : "ltr";
  };

  const titleDirection = getTextDirection(displayData.title);
  const descriptionDirection = getTextDirection(displayData.description);

  const handleBackToFeed = () => {
    navigate(-1);
  };

  const handleNextImage = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!visualMedia || visualMedia.length <= 1) return;
    
    setTransitionDirection('left');
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % visualMedia.length);
      setIsTransitioning(false);
      setTransitionDirection(null);
    }, 300);
  };

  const handlePrevImage = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!visualMedia || visualMedia.length <= 1) return;
    
    setTransitionDirection('right');
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex - 1 + visualMedia.length) % visualMedia.length);
      setIsTransitioning(false);
      setTransitionDirection(null);
    }, 300);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.touches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    
    const difference = touchStartX - touchEndX;
    const minSwipeDistance = 50;
    
    if (difference > minSwipeDistance && visualMedia.length > 1) {
      handleNextImage();
    } else if (difference < -minSwipeDistance && visualMedia.length > 1) {
      handlePrevImage();
    }
    
    setTouchStartX(null);
    setTouchEndX(null);
  };

  const openMediaViewer = (index: number) => {
    setMediaViewerIndex(index);
    setShowMediaViewer(true);
  };
  
  const handleLikeToggle = () => {
    if (!isLoggedIn()) {
      toast.error("Please sign in to like this want");
      navigate('/signin');
      return;
    }
    
    if (liking) return; // Prevent multiple clicks
    
    setLiking(true);
    setIsLiked(!isLiked);
    setLikeCount(prev => isLiked ? prev - 1 : prev + 1);
    setLiking(false);
    toast.success(isLiked ? "Removed like" : "Added like");
  };
  
  const handleCommentClick = () => {
    if (!isLoggedIn()) {
      toast.error("Please sign in to comment");
      navigate('/signin');
      return;
    }
    
    setIsCommentModalOpen(true);
  };

  // Add this function to safely render HTML content
  const renderHTML = (html: string) => {
    return { __html: html };
  };

  const handleEdit = () => {
    console.log("Will data before edit:", displayData);
    
    // Navigate to the correct edit route
    navigate(`/will/${displayData.id}/edit`, {
      state: {
        will: displayData
      }
    });
  };
  
  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      
      <main className="container mx-auto px-4 pt-16 pb-6">
        {/* Instagram-style card - More compact */}
        <div className="max-w-xl mx-auto">
          {/* Back button */}
          <button 
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            <span className="text-sm">Back</span>
          </button>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-4 animate-fade-in">
            {/* Card header */}
            <div className="flex items-center justify-between p-3 border-b border-gray-100">
              <Link to={`/profile/${displayData.wanterId}`} className="flex items-center">
                <Avatar className="h-8 w-8 rounded-full border border-gray-200 mr-2">
                  {will?.wanterProfilePicture ? (
                    <img 
                      src={getProfilePictureUrl(will.wanterProfilePicture)} 
                      alt={displayData.wanterName} 
                      className="h-full w-full object-cover rounded-full"
                    />
                  ) : (
                    <div className="h-full w-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white text-sm font-bold">
                      {displayData.wanterName ? displayData.wanterName.charAt(0).toUpperCase() : '?'}
                    </div>
                  )}
                </Avatar>
                <div>
                  <span className="font-medium text-sm">{displayData.wanterName || 'Anonymous'}</span>
                  {displayData.location && (
                    <div className="text-xs text-gray-500">📍 {displayData.location}</div>
                  )}
                </div>
              </Link>

              <div className="flex items-center">
                <span className="text-xs text-gray-500 mr-2">
                  {formatDate(displayData.createdAt)}
                </span>
                
                <ShareMenu 
                  willId={displayData.id} 
                  title={displayData.title} 
                  position="bottom"
                  iconSize={18}
                  className="mr-1"
                />

                {isOwner() && (
                  <DropdownMenu>
                    <DropdownMenuTrigger className="p-1.5 hover:bg-gray-100 rounded-full">
                      <MoreVertical className="h-4 w-4 text-gray-600" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-white border border-gray-200 shadow-md">
                      <DropdownMenuItem 
                        onClick={handleEdit}
                        className="cursor-pointer"
                      >
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="text-red-600 cursor-pointer hover:bg-red-600 hover:text-white"
                        onClick={() => setShowDeleteDialog(true)}
                      >
                        <Trash className="h-4 w-4 mr-2" />
                        Delete Want
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>

            {/* Media display - Instagram style - Enhanced - More compact */}
            {visualMedia.length > 0 && currentMedia ? (
              <div className="relative bg-gray-100 aspect-square mx-auto" style={{ maxWidth: '400px', width: '100%' }}>
                <div 
                  className="w-full h-full flex justify-center items-center"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <div 
                    className={`transition-transform duration-300 cursor-pointer w-full h-full flex items-center justify-center ${
                      isTransitioning 
                        ? transitionDirection === 'left' 
                          ? 'translate-x-[-100%]' 
                          : 'translate-x-[100%]' 
                        : ''
                    }`}
                    onClick={() => openMediaViewer(currentImageIndex)}
                  >
                    {currentMedia.type === 'image' || currentMedia.mimeType?.startsWith('image/') ? (
                      <img 
                        src={currentMedia.url} 
                        alt="Want image"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="relative w-full h-full">
                        <img 
                          src={currentMedia.url.replace(/\.[^/.]+$/, ".jpg")} 
                          alt="Video thumbnail"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = "https://via.placeholder.com/400x400?text=Video";
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                          <div className="h-14 w-14 bg-white bg-opacity-80 rounded-full flex items-center justify-center">
                            <Play className="h-7 w-7 text-gray-800" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {visualMedia.length > 1 && (
                  <>
                    <button 
                      onClick={handlePrevImage}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 w-7 h-7 bg-white bg-opacity-70 rounded-full flex items-center justify-center shadow-sm"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button 
                      onClick={handleNextImage}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 w-7 h-7 bg-white bg-opacity-70 rounded-full flex items-center justify-center shadow-sm"
                    >
                      <ChevronRight size={16} />
                    </button>
                    
                    <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                      {visualMedia.map((_, index) => (
                        <span 
                          key={index}
                          className={`inline-block w-1.5 h-1.5 rounded-full ${
                            index === currentImageIndex ? 'bg-blue-500' : 'bg-white bg-opacity-70'
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              // Text display when no media is available
              <div className="w-full aspect-square flex items-center justify-center p-6 text-center bg-gradient-to-br from-blue-50 to-indigo-100 border border-gray-200 mx-auto" style={{ maxWidth: '400px' }}>
                <div 
                  className="text-gray-700 font-medium overflow-hidden overflow-ellipsis line-clamp-10 max-w-[90%]"
                  dir={descriptionDirection}
                  style={{ textAlign: descriptionDirection === "rtl" ? "right" : "left" }}
                  dangerouslySetInnerHTML={renderHTML(displayData.description || displayData.title)}
                />
              </div>
            )}

            {/* Content section - more compact */}
            <div className="p-3">
              {/* Title and category only (removed urgency) */}
              <div className="mb-2">
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-800 flex items-center">
                    <Tag className="h-3 w-3 mr-0.5" />
                    {displayData.category}
                  </span>
                </div>
                
                <h1 
                  className="text-lg font-semibold text-gray-900"
                  dir={titleDirection}
                  style={{ textAlign: titleDirection === "rtl" ? "right" : "left" }}
                >
                  {displayData.title}
                </h1>
              </div>
              
              {/* Reward - highlighted */}
              {displayData.reward && (
                <div className="mb-2">
                  <span className="text-xl font-bold text-black bg-yellow-200 px-2 py-0.5 rounded-md inline-block">
                    {displayData.reward}
                  </span>
                </div>
              )}
              
              {/* Description - Truncated for compact view */}
              <div 
                className="prose max-w-none text-gray-700 mb-3 text-sm line-clamp-3"
                dir={descriptionDirection}
                style={{ textAlign: descriptionDirection === "rtl" ? "right" : "left" }}
                dangerouslySetInnerHTML={renderHTML(displayData.description)}
              />
              
              <div className="flex flex-wrap gap-2 mb-2">
                {/* Due date */}
                {displayData.fulfillmentTime && (
                  <div className="flex items-center text-xs text-gray-600">
                    <Clock className="h-3 w-3 mr-1" />
                    <span>Due: {new Date(displayData.fulfillmentTime).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}</span>
                  </div>
                )}
                
                {/* Skills - Compact horizontal display */}
                {displayData.skills && displayData.skills.length > 0 && (
                  <div className="flex flex-wrap gap-1 items-center">
                    <span className="text-xs text-gray-600">Skills:</span>
                    {displayData.skills.slice(0, 3).map((skill) => (
                      <span key={skill} className="px-1.5 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700">
                        {skill}
                      </span>
                    ))}
                    {displayData.skills.length > 3 && (
                      <span className="text-xs text-gray-500">+{displayData.skills.length - 3} more</span>
                    )}
                  </div>
                )}
              </div>
              
              {/* Documents/attachments - simplified */}
              {documentMedia.length > 0 && (
                <div className="mb-3">
                  <div className="flex items-center mb-1">
                    <h3 className="text-xs font-medium text-gray-700">Attachments</h3>
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      {documentMedia.length}
                    </span>
                  </div>
                  <div className="space-y-1.5 bg-gray-50 p-2 rounded-lg border border-gray-100">
                    {documentMedia.map((doc) => (
                      <DownloadableFileAttachment 
                        key={doc.id || `doc-${Math.random()}`} 
                        file={doc} 
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Action buttons - more compact */}
            <div className="px-3 py-2 border-t border-gray-100 flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <button 
                  onClick={handleLikeToggle}
                  className="flex items-center focus:outline-none"
                  disabled={liking}
                >
                  <Heart 
                    size={18} 
                    className={`${isLiked ? 'fill-red-500 text-red-500' : 'text-gray-600'} mr-1`} 
                  />
                  <span className="text-sm">{likeCount > 0 ? likeCount : "Like"}</span>
                </button>
                
                <button 
                  onClick={handleCommentClick}
                  className="flex items-center text-gray-600 focus:outline-none"
                >
                  <MessageCircle size={18} className="mr-1" />
                  <span className="text-sm">Comment</span>
                </button>
              </div>
              
              <Link 
                to={isLoggedIn() ? `/offer/${id}` : '/signin'}
                state={isLoggedIn() ? { will: displayData } : { redirectTo: `/will/${id}` }}
                className="flex items-center px-3 py-1.5 bg-[#222222] text-white text-sm font-medium rounded-lg hover:bg-[#333333] transition-colors"
                onClick={(e) => {
                  if (!isLoggedIn()) {
                    e.preventDefault();
                    toast.error("Please sign in to make an offer");
                    navigate('/signin', { state: { redirectTo: `/will/${id}` } });
                  }
                }}
              >
                <Wand2 size={15} className="mr-1" />
                Make an Offer
              </Link>
            </div>
          </div>
        </div>
      </main>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your want
              and remove it from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showMediaViewer && (
        <MediaViewer
          media={visualMedia}
          initialIndex={mediaViewerIndex}
          onClose={() => setShowMediaViewer(false)}
        />
      )}
      
      {/* Comment Modal */}
      {isLoggedIn() && (
        <CommentModal 
          isOpen={isCommentModalOpen}
          onClose={() => setIsCommentModalOpen(false)}
          willId={id || ''}
          willTitle={displayData.title}
        />
      )}

      {/* Offer Form Modal */}
      <OfferFormModal
        isOpen={isOfferModalOpen}
        onClose={() => setIsOfferModalOpen(false)}
        willId={id || ''}
        willTitle={displayData.title}
        willMedia={displayData.media || []}
      />
    </div>
  );
};

export default WillView;
